import Box from '@mui/material/Box';
import React, { useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUserService } from '../../services/user.service';

// Beacon function type
type BeaconFunction = {
  (method: string, options?: any, data?: any): void;
  readyQueue: Array<{ method: string; options?: any; data?: any }>;
  [key: string]: any;
};

// Add beacon to window object type
declare global {
  interface Window {
    Beacon?: BeaconFunction;
  }
}

// Function to identify a user with Beacon
export const identifyBeaconUser = async (email: string, userService: any): Promise<void> => {
  try {
    const signature = await userService.generateEmailSignature(email);
    
    if (window.Beacon) {
      window.Beacon('identify', {
        email: email,
        signature: signature,
      });
    }
  } catch (error) {
    throw error;
  }
};

// HelpScout Beacon widget component
const ChatWidget: React.FC = () => {
  const { currentUser } = useAuth();
  const userService = useUserService();

  // Initialize Beacon only on desktop screens
  useEffect(() => {
    // Initialize Help Scout Beacon
    const initializeBeacon = () => {
      // Function to load the Beacon script
      const loadBeacon = (): void => {
        const scripts = document.getElementsByTagName('script')[0];
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://beacon-v2.helpscout.net';
        scripts.parentNode?.insertBefore(script, scripts);
      };

      // Create the Beacon function if it doesn't exist
      const beaconFunction = (method: string, options?: any, data?: any): void => {
        if (window.Beacon && window.Beacon.readyQueue) {
          window.Beacon.readyQueue.push({ method, options, data });
        }
      };
      
      // Initialize Beacon
      if (!window.Beacon) {
        beaconFunction.readyQueue = [] as Array<{ method: string; options?: any; data?: any }>;
        window.Beacon = beaconFunction;
      }
      
      // Load Beacon based on document readiness
      if (document.readyState === 'complete') {
        loadBeacon();
      } else {
        window.addEventListener('load', loadBeacon, false);
      }

      // Initialize with beacon ID once loaded
      setTimeout(() => {
        if (window.Beacon) {
          window.Beacon('init', '777f0c8c-6578-4c8f-b9f7-655a77f14392');
          
          // Configure the beacon to ensure it's visible on all pages
          window.Beacon('config', {
            showPrefilledCustomFields: false,
            display: {
              style: 'icon',
              position: 'left',
              zIndex: 10000
            }
          });
          
          // Debug: Log chat widget position for alignment
          setTimeout(() => {
            const beaconElements = [
              document.getElementById('beacon-container'),
              document.querySelector('.beacon-fab'),
              document.querySelector('[id*="beacon"]'),
              document.querySelector('[class*="beacon"]'),
              document.querySelector('.hsds-beacon'),
            ].filter(el => el !== null);
            
            beaconElements.forEach((el, index) => {
              if (el) {
                const rect = el.getBoundingClientRect();
                const computedStyle = window.getComputedStyle(el);
                console.log(`Chat widget element ${index}:`, {
                  element: el,
                  left: computedStyle.left,
                  bottom: computedStyle.bottom,
                  rect: { left: rect.left, bottom: window.innerHeight - rect.bottom }
                });
              }
            });
          }, 2000);
        }
      }, 500);
    };

    // Only initialize beacon on desktop screens (sm breakpoint = 600px)
    const mediaQuery = window.matchMedia('(min-width: 600px)');
    
    // Initialize beacon if currently on desktop
    if (mediaQuery.matches) {
      initializeBeacon();
    }

    // Listen for screen size changes
    const handleScreenChange = (e: MediaQueryListEvent) => {
      if (e.matches && !window.Beacon) {
        // Switching to desktop and beacon not initialized
        initializeBeacon();
      } else if (!e.matches && window.Beacon) {
        // Switching to mobile and beacon exists - destroy it
        try {
          window.Beacon('destroy');
          delete window.Beacon;
        } catch (error) {
          console.log('Error destroying Beacon widget:', error);
        }
      }
    };

    mediaQuery.addEventListener('change', handleScreenChange);

    // Cleanup function
    return () => {
      mediaQuery.removeEventListener('change', handleScreenChange);
      if (window.Beacon) {
        try {
          window.Beacon('destroy');
        } catch (e) {
          // Error destroying Beacon widget
        }
      }
    };
  }, []); // Empty deps - only run once

  // Handle user identification separately
  useEffect(() => {
    // Wait a bit to ensure Beacon is initialized
    const timer = setTimeout(() => {
      if (window.Beacon && currentUser?.email) {
        identifyBeaconUser(currentUser.email, userService);
      } else if (window.Beacon && !currentUser) {
        // User logged out, clear Beacon identification
        window.Beacon('logout');
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentUser, userService]);

  // The component doesn't render any visible UI itself
  // It just initializes the HelpScout Beacon widget
  return (
    <Box 
      sx={{
        position: 'fixed',
        bottom: '20px',
        left: { xs: '20px', sm: '80px' }, // Align with header content area
        zIndex: 9999,
      }} 
    />
  );
};

export default ChatWidget;
